import { useEffect, useRef } from 'react';
import { loadLastRollLocal, StoredLastRoll } from '../components/DiceRoller/DiceRoller';
import { LAST_ROLL_TTL_MS } from '../consts/consts';
import { joinRoom, listenPlayers, publishRoll } from '../utils/roomClient';

const isFresh = (entry: StoredLastRoll | null) => entry && (Date.now() - entry.timestamp <= LAST_ROLL_TTL_MS);

type Params = {
    roomId: string | null;
    playerId: string;
    playerName: string;
    authLoading: boolean;
    setPlayers: (players: Record<string, any>) => void;
};

export function useRoomConnection({
    roomId,
    playerId,
    playerName,
    authLoading,
    setPlayers,
}: Params) {
    const listenerRef = useRef<(() => void) | null>(null);
    const republishedByRoomAndPlayerRef = useRef<Record<string, boolean>>({});

    useEffect(() => {
        let mounted = true;
        let heartbeatCleanup: (() => void) | null = null;

        if (listenerRef.current) {
            try { listenerRef.current(); } catch { /* ignore */ }
            listenerRef.current = null;
        }
        setPlayers({});

        if (!roomId || authLoading) return;

        (async () => {
            let joined = false;
            try {
                const joinCleanup = await joinRoom(roomId, playerId, playerName, null);
                if (!mounted) {
                    try { joinCleanup(); } catch { /* ignore */ }
                    return;
                }
                heartbeatCleanup = joinCleanup;
                joined = true;
            } catch (err) {
                console.error('joinRoom failed', err);
            }

            if (!mounted || !joined) return;

            try {
                const unsub = listenPlayers(roomId, (p) => {
                    if (!mounted) return;
                    setPlayers(p ?? {});
                });
                listenerRef.current = typeof unsub === 'function' ? unsub : null;
            } catch (err) {
                console.error('listenPlayers failed', err);
            }

            const republishKey = `${roomId}::${playerId}`;
            if (!republishedByRoomAndPlayerRef.current[republishKey]) {
                try {
                    const stored = loadLastRollLocal();
                    if (stored && isFresh(stored) && stored.roomId === roomId) {
                        await publishRoll(roomId, playerId, {
                            ...stored.payload,
                            timestamp: stored.timestamp,
                        });
                    }
                    republishedByRoomAndPlayerRef.current[republishKey] = true;
                } catch (err) {
                    console.error('republish last roll failed', err);
                }
            }
        })();

        return () => {
            mounted = false;
            if (listenerRef.current) {
                try { listenerRef.current(); } catch { /* ignore */ }
                listenerRef.current = null;
            }
            if (heartbeatCleanup) {
                try { heartbeatCleanup(); } catch { /* ignore */ }
                heartbeatCleanup = null;
            }
        };
    }, [roomId, playerId, playerName, authLoading, setPlayers]);
}
